import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import { take } from 'rxjs';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { TellentPositionsDataFacade } from '@core/tellent-positions-logic';
import {
  TellentPosition,
  TellentPositionMember,
  UnassignMemberFromPositionDialogResult,
} from '@core/tellent-positions-types';
import { resolveMemberFullName } from '@core/tellent-util';
import {
  AvatarComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  NotificationsController,
} from '@core/user-interface-legacy';

interface UnassignMemberFromPositionDialogData {
  positionData: TellentPosition;
  member: TellentPositionMember;
}

@Component({
  selector: 'tlc-unassign-member-from-position-dialog',
  templateUrl: './unassign-member-from-position-dialog.component.html',
  styleUrls: ['./unassign-member-from-position-dialog.component.less'],
  imports: [
    DialogBodyComponent,
    TranslatePipe,
    ButtonComponent,
    DialogHeaderComponent,
    DialogFooterComponent,
    ReactiveFormsModule,
    ButtonLoadingDirective,
    AvatarComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnassignMemberFromPositionDialogComponent implements DialogComponentI<
  UnassignMemberFromPositionDialogData,
  UnassignMemberFromPositionDialogResult
> {
  private readonly positionsDataFacade = inject(TellentPositionsDataFacade);
  private readonly dialogRef = inject(DialogRef);
  private readonly i18n = inject(I18nService);
  private readonly notifications = inject(NotificationsController);

  public loading = signal<boolean>(false);
  public positionData = signal<TellentPosition | null>(null);
  public member = signal<TellentPositionMember | null>(null);

  public memberFullName = computed<string>(() => resolveMemberFullName(this.member()!));

  public setDialogData(data: UnassignMemberFromPositionDialogData, triggerUpdate: boolean): void {
    this.positionData.set(data.positionData);
    this.member.set(data.member);
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  public unassign(): void {
    this.loading.set(true);
    this.positionsDataFacade
      .unassignMembersFromPosition(this.positionData()!.guid, [this.member()!.guid])
      .pipe(take(1))
      .subscribe(result => {
        this.loading.set(false);
        if (result.isSuccess) {
          this.notifications.success(
            this.i18n.translate(
              'tlc.positions.position_details_dialog.tabs.employees.unassign_members_dialog.notifications.success',
            ),
          );
          this.dialogRef.close({ success: true });
        } else {
          this.notifications.error(
            this.i18n.translate(
              'tlc.positions.position_details_dialog.tabs.employees.unassign_members_dialog.notifications.error',
            ),
          );
        }
      });
  }
}
